import React from "react";
import { UserNotificationsEnum as N } from "../auth/userNotifications.enum";
import { UserRolesEnum } from "../auth/userRoles.enum";
import AuthService from "../../base/services/authentication.service";

// Figma "Notifications icon types": notifications fall into five categories by the element
// they refer to (sign-ups, workspaces, assessments, tickets, submissions), one icon each.
// The icon's colour shows the role the notification relates to.
const CATEGORY_BY_TYPE = {
   [N.AdminRegistration]: "signup",
   [N.AdminExpertRequest]: "signup",

   [N.AdminWorkspaceRequest]: "workspace",
   [N.NgoApprovedWorkspace]: "workspace",
   [N.NgoRejectedWorkspace]: "workspace",

   [N.AdminAssessmentRequest]: "assessment",
   [N.NgoApprovedAssessment]: "assessment",
   [N.NgoRejectedAssessment]: "assessment",
   [N.NgoCompletedAssessment]: "assessment",
   [N.ExpertAssessmentRequest]: "assessment",
   // Manager "New self-assessment assigned" (and its removal counterpart).
   [N.ManagerAddedAssignee]: "assessment",
   [N.ManagerRemovedAssignee]: "assessment",

   [N.AdminAddedTicket]: "ticket",
   [N.AdminAddedTicketMessage]: "ticket",
   [N.NgoAddedTicketMessage]: "ticket",

   [N.ManagerRequestedSubmit]: "submission",
   [N.ManagerApprovedSubmitRequest]: "submission",
   [N.ManagerRejectedSubmitRequest]: "submission",
   [N.StaffAddedAssignee]: "submission",
   [N.StaffRemovedAssignee]: "submission",
   [N.StaffApprovedSubmitRequest]: "submission",
   [N.StaffRejectedSubmitRequest]: "submission",
   [N.ApproverRequestedApproval]: "submission"
};

// Titles from the design, one per type. The backend's own titles are free text with
// typos and entity names in them ("You have ben assigned to assessment: X"); the body
// still carries those names.
const TITLE_KEY_BY_TYPE = {
   [N.AdminRegistration]: "notification-title-org-signup",
   [N.AdminExpertRequest]: "notification-title-expert-signup",
   [N.AdminWorkspaceRequest]: "notification-title-workspace-join-request",
   [N.NgoApprovedWorkspace]: "notification-title-workspace-approved",
   [N.NgoRejectedWorkspace]: "notification-title-workspace-rejected",
   [N.AdminAssessmentRequest]: "notification-title-guided-request",
   [N.NgoApprovedAssessment]: "notification-title-guided-approved",
   [N.NgoRejectedAssessment]: "notification-title-guided-rejected",
   [N.NgoCompletedAssessment]: "notification-title-guided-completed",
   [N.ManagerAddedAssignee]: "notification-title-self-assigned",
   [N.ManagerRemovedAssignee]: "notification-title-self-unassigned",
   [N.ExpertAssessmentRequest]: "notification-title-expert-assigned",
   [N.AdminAddedTicket]: "notification-title-ticket-created",
   [N.AdminAddedTicketMessage]: "notification-title-ticket-updated",
   [N.NgoAddedTicketMessage]: "notification-title-ticket-updated",
   [N.ManagerRequestedSubmit]: "notification-title-submission-review",
   [N.ManagerApprovedSubmitRequest]: "notification-title-submission-approved-by-approver",
   [N.ManagerRejectedSubmitRequest]: "notification-title-submission-rejected-by-approver",
   [N.StaffAddedAssignee]: "notification-title-module-assigned",
   [N.StaffRemovedAssignee]: "notification-title-module-unassigned",
   [N.StaffApprovedSubmitRequest]: "notification-title-submission-approved",
   [N.StaffRejectedSubmitRequest]: "notification-title-submission-rejected",
   [N.ApproverRequestedApproval]: "notification-title-submission-approval"
};

/** The design's title for the notification's type; unknown types keep the backend's. */
export const getNotificationTitle = (notification, t) => {
   const key = TITLE_KEY_BY_TYPE[notification?.notificationType];
   return key ? t(key) : notification?.notificationTitle;
};

// Role colours from the design's legend. Coloured by the viewer's active role: a user only
// receives notifications in that role, and the same type (e.g. "Submission approved by
// approver") is shown in the colour of whoever is looking at it.
const ROLE_COLORS = [
   [[UserRolesEnum.SuperAdmin], "#4158CF"],
   [[UserRolesEnum.NationalOrganization, UserRolesEnum.InternationalOrganization], "#5492FF"],
   [[UserRolesEnum.Manager], "#20BAB7"],
   [[UserRolesEnum.Staff], "#EE903A"],
   [[UserRolesEnum.Consultant], "#EE3A58"],
   [[UserRolesEnum.Approver], "#A7AFD4"]
];
// Donor isn't in the legend.
const DEFAULT_COLOR = "#4158CF";

export const getNotificationRoleColor = () =>
   ROLE_COLORS.find(([roles]) => AuthService.isAuthorized(roles))?.[1] || DEFAULT_COLOR;

const stroke = {
   stroke: "currentColor",
   strokeWidth: 1.3,
   strokeLinecap: "round",
   strokeLinejoin: "round",
   fill: "none"
};

const PATHS = {
   signup: (
      <>
         <circle cx="8" cy="6.5" r="3" {...stroke} />
         <path d="M2.5 16.5C2.5 13.5 5 11.5 8 11.5C9.2 11.5 10.3 11.8 11.2 12.4" {...stroke} />
         <path d="M15 11.5V16.5M12.5 14H17.5" {...stroke} />
      </>
   ),
   workspace: (
      <>
         <rect x="2.5" y="2.5" width="6" height="8" rx="1.2" {...stroke} />
         <rect x="11.5" y="2.5" width="6" height="5" rx="1.2" {...stroke} />
         <rect x="11.5" y="10.5" width="6" height="7" rx="1.2" {...stroke} />
         <rect x="2.5" y="13.5" width="6" height="4" rx="1.2" {...stroke} />
      </>
   ),
   assessment: (
      <>
         <path
            d="M11.5 2.5H5.5C4.7 2.5 4 3.2 4 4V16C4 16.8 4.7 17.5 5.5 17.5H14.5C15.3 17.5 16 16.8 16 16V7L11.5 2.5Z"
            {...stroke}
         />
         <path d="M11.5 2.5V7H16" {...stroke} />
         <path d="M6.8 11L7.8 12L9.6 10.2M6.8 14.6L7.8 15.6L9.6 13.8M11.6 11.2H13.4M11.6 14.8H13.4" {...stroke} />
      </>
   ),
   ticket: (
      <>
         <path
            d="M2.5 6C2.5 5.2 3.2 4.5 4 4.5H16C16.8 4.5 17.5 5.2 17.5 6V8C16.4 8 15.5 8.9 15.5 10C15.5 11.1 16.4 12 17.5 12V14C17.5 14.8 16.8 15.5 16 15.5H4C3.2 15.5 2.5 14.8 2.5 14V12C3.6 12 4.5 11.1 4.5 10C4.5 8.9 3.6 8 2.5 8V6Z"
            {...stroke}
         />
         <path d="M8 8.5H12M8 11.5H12" {...stroke} />
      </>
   ),
   submission: (
      <>
         <rect x="4" y="3.5" width="12" height="14" rx="1.5" {...stroke} />
         <path d="M7.5 3.5V2.5H12.5V3.5" {...stroke} />
         <path d="M7 8H13M7 11H13M7 14H11" {...stroke} />
      </>
   ),
   // Unknown/future types.
   default: (
      <>
         <path
            d="M14.5 7C14.5 4.5 12.5 2.5 10 2.5C7.5 2.5 5.5 4.5 5.5 7C5.5 12.5 3.5 14 3.5 14H16.5C16.5 14 14.5 12.5 14.5 7Z"
            {...stroke}
         />
         <path d="M11.3 16.5C11 17.1 10.5 17.5 10 17.5C9.5 17.5 9 17.1 8.7 16.5" {...stroke} />
      </>
   )
};

export function NotificationTypeIcon({ type }) {
   const category = CATEGORY_BY_TYPE[type] || "default";
   return (
      <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
         {PATHS[category]}
      </svg>
   );
}
